/**
 * The thread column: the reading pane, docked beside the explorations sidebar
 * with the canvas to its right.
 *
 * THE CHAT OWNS THE COLUMN. The store contract is still the rail's own —
 * `railRight` opens it, `rightTab` picks the surface, every entry point that
 * wrote those still lands here — but the Node tab is now the conversation
 * (`ui/Feed.tsx`): passes streaming their reasoning as they run, each run one
 * growing message, the reader's questions and answers between them, the
 * composer at the foot. The inspector survives whole as the NODE CARD at the
 * head of the conversation: it opens when a node is selected, and it shares
 * ONE scroll with the conversation beneath it — the card used to sit in a
 * nested region of its own, a 430px keyhole over 2,000px of card, and the
 * study that measured it found the Ask box pushed off the screen by it. The
 * input stays pinned under the scroll. D25's finding stands — reading detached
 * from its node fails — and the card folds to one line when asked.
 *
 * Selection drives the scroll and never the reverse. A growing graph must not
 * move the reading under the reader, so the card is seated on the anchor id
 * alone — nodes landing during a run leave the reading exactly where it is,
 * and the chat keeps the same promise its own way (it follows its tail only
 * while the reader is at the tail, or when the reader started what is there).
 *
 * Below the phone breakpoint the pane is a HALF-HEIGHT bottom sheet: the graph
 * stays visible above it, the composer yields to it, and a node tap reopens it
 * on the card's claim. The feed carries its own ask box at that width, pinned
 * like the composer, because the composer strip is exactly what the sheet
 * displaced.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useStore } from '../core/store'
import { threadFor } from '../core/thread'
import { claimRestatesLabel, kindChannelOf, kindLabelOf, type ReasonNode } from '../core/types'
import { Icon } from './components/Icon'
import { Chevron } from './components/Chevron'
import { GlassPane } from './components/GlassShell'
import { Overview } from './Overview'
import { RunReport } from './RunReport'
import { Feed } from './Feed'
import { Fold, NodeCard, scrollToRewriteEditor } from './NodeCard'
import { focusCardTitle } from './cardFocus'
import { ThreadFoot } from './ThreadFoot'
import { closeDetails, openDetails, useDetailsFor } from './cardDetails'
import { Composer } from './Composer'
import { useReadFirst } from './SharedCopyBanner'
import { nameOrUntitled } from '../core/name'
import { reportClaimCount } from '../core/reportSections'
import { STATUS_WORD, claimCount } from '../core/vocabulary'
import { DEMO_SESSION_ID } from '../core/persist'
import { useGraphFrame } from '../canvas/useGraphFrame'
import { useMedia } from './components/useMedia'
import {
  PHONE_SHEET_SHARE,
  THREAD_STEP,
  THREAD_W_VAR,
  stepThreadWidth,
  threadBounds,
} from './threadPane'
import { SIDEBAR_INSET_VAR, SIDEBAR_SLIDE_VAR } from './sidebarPane'
import { PaneSeam } from './components/PaneSeam'
import { evidenceStaleOf } from '../core/rebase'
import { endingOf } from '../core/endings'
import { rewriteAuthorship } from '../core/rewrite'
import { secondLookChange } from '../core/statusWords'
import { stateClauses } from '../core/stateClauses'
import { refutedUnder, type RefutedUnder } from '../core/taint'
import { scrollToOutcomeForm } from './Grounding'

/**
 * Narrow screens: the pane drops to a bottom sheet — full width, one scroll,
 * the graph left visible above it. A full-height column here would bury the
 * graph AND the composer behind glass. The composer yields to the sheet (App
 * hides it while the rail is open at this width) and a node tap reopens the
 * sheet, so the two never fight for the same corner.
 *
 * HALF the screen, and a dynamic half. At 64vh the sheet left the graph a
 * strip, and its card was a 112px keyhole under its own header, its own
 * footer and the ask box; a half keeps the map readable above and the claim
 * readable below. `dvh` rather than `vh` because a phone's `vh` is the
 * viewport with its toolbars retracted, which is taller than what is on
 * screen whenever they are showing. `PHONE_SHEET_SHARE` is the same number for
 * anything that must know how much of the stage the sheet covers.
 */
const SHEET: CSSProperties = {
  position: 'absolute',
  left: 8,
  right: 8,
  bottom: 'max(8px, env(safe-area-inset-bottom))',
  top: 'auto',
  height: `${PHONE_SHEET_SHARE * 100}dvh`,
  zIndex: 'var(--z-float)' as unknown as number,
  pointerEvents: 'auto',
}

/**
 * Whether the seed's card is open when NOTHING is selected, outliving the pane
 * the way the composer's draft does (D36's latch): ⌘] unmounts this component,
 * and a reader's choice about the card should not be undone by the very key
 * that brings the column back. A selection opens its own card regardless —
 * this only decides the fallback, where the chat owns the column and the
 * seed's card is the on-demand guest.
 */
let seedOpenLatch = false

/**
 * The column's head (D362), in two rows. On top, where the reader is:
 * "Explorations", the level above, as a press back to it; the exploration's name,
 * as the sidebar names its row; and what it is, as marks — the worked example's
 * pill, and the lock a shared copy wears while it is read first, which nothing
 * changes until it is taken over. The control that puts the column away ends the row. Under it, the
 * pane's two surfaces as underline tabs standing on a hairline, as D346 drew them,
 * the second with the report's claim count beside its name, so the tab and what
 * it opens say one number. Runs is a surface this app has and the row does not —
 * it opens from the run pod and renders here with neither tab lit. Ask is not
 * among them: a conversation renders beside the thing it is about. Overview is the
 * report read in place, still `'breakthroughs'` in the store, because that value
 * is a stored preference and a rename would need a migration to say nothing new.
 */
export function ThreadHead({ narrow, surface }: { narrow: boolean; surface: string }) {
  const setRightTab = useStore((s) => s.setRightTab)
  const setRail = useStore((s) => s.setRail)
  const readFirst = useReadFirst()
  const seed = useStore((s) => s.session.seed)
  const named = useStore((s) => s.session.name)
  const demo = useStore((s) => s.session.id === DEMO_SESSION_ID)
  const title = nameOrUntitled({ seed, name: named })
  const claims = useStore((s) => reportClaimCount(s.session))
  const pokeExplorations = useStore((s) => s.pokeExplorations)
  // Up a level. On a phone this pane is a sheet over the field and the list is an
  // overlay over the same field, so the sheet goes down first.
  const toExplorations = () => {
    if (narrow) setRail(false)
    pokeExplorations()
  }
  return (
    <div className="ttabs" data-narrow={narrow || undefined}>
      <div className="thead">
        <nav className="tcrumbs" aria-label="Where this exploration is">
          <button
            className="tcrumb-root"
            title="Explorations — every exploration in this browser"
            onClick={toExplorations}
          >
            Explorations
          </button>
          <Icon name="chevron" size={13} className="tcrumb-sep" />
          <b className="tcrumb-title" title={seed} aria-current="page">
            {title}
          </b>
          {demo && <span className="tpill">Demo</span>}
          {/* One mark for a copy read first, not two: the lock's hover says it
              is a shared copy, and so does the copy's own line on the field. A
              second pill pushed the name out of a column at its narrowest. */}
          {readFirst && (
            <span
              className="tlock"
              title={`${STATUS_WORD.sharedCopy}, read first: nothing runs or changes until you continue working on it.`}
            >
              <Icon name="lock" size={13} />
              <span className="tlock-word">Read-only</span>
            </span>
          )}
        </nav>
        {/* One control, two faces. On the phone the pane IS a sheet and
              closing it is what the gesture means; on the desktop it is a COLUMN,
              and what the press does is fold it away and give the field the
              space — with a tab left at the seam to bring it back. Either way it
              is the one door, `setRail(false)`: ⌘] and every entry point that
              opens this column keep working because nothing new was invented for
              open and closed. */}
        <button
          className="insp-close"
          aria-label={narrow ? 'Close the thread' : 'Collapse the thread'}
          title={narrow ? 'Close' : 'Collapse  ⌘]'}
          onClick={() => setRail(false)}
        >
          <Icon
            name={narrow ? 'close' : 'chevron'}
            size={13}
            // Pointing the way the column goes: it folds toward the sidebar
            // and the field takes the space it leaves.
            style={narrow ? undefined : { transform: 'rotate(180deg)' }}
          />
        </button>
      </div>
      <div className="ttabs-list" role="tablist" aria-label="What the column shows">
        <button
          role="tab"
          className="ttab"
          aria-selected={surface === 'node'}
          onClick={() => setRightTab('node')}
        >
          <span className="ttab-word">Thread</span>
        </button>
        <button
          role="tab"
          className="ttab"
          aria-selected={surface === 'breakthroughs'}
          onClick={() => setRightTab('breakthroughs')}
        >
          <span className="ttab-word">Overview</span>
          <span className="tcount" title={claimCount(claims)}>
            {claims}
          </span>
        </button>
      </div>
    </div>
  )
}

export function Thread({
  left,
  width,
  stageW,
}: {
  left: number
  width: number
  /** The stage's own width, for the separator's bounds — the same number App
   *  clamps the column with, so the handle can never offer a width the pane
   *  would then be given differently. */
  stageW: number
}) {
  const rightTab = useStore((s) => s.rightTab)
  const chat = useStore((s) => s.chat)
  const setRail = useStore((s) => s.setRail)
  const narrow = useMedia('(max-width: 767px)')
  const readFirst = useReadFirst()
  // The foot's Focus and Prune act on the claim held, so on a desktop they stand
  // under its card — with its details open — and the field card carries Focus
  // otherwise (D354).
  const selectedId = useStore((s) => s.selectedId)
  const details = useDetailsFor()
  const footShown = readFirst || (details !== null && details === selectedId)

  /**
   * `ask` is no longer a surface of its own — it is a POINTER at the surface
   * where the conversation's subject lives. A question about a node or the
   * graph resolves to the thread, where the feed holds the conversation; one
   * about a candidate resolves to the Overview, where its card is. The store
   * still writes `rightTab: 'ask'` from `askAbout`, untouched — this reads
   * that write as the instruction it always was ("show me the conversation")
   * and answers it with a place.
   */
  const surface =
    rightTab === 'ask' ? (chat?.kind === 'bridge' ? 'breakthroughs' : 'node') : rightTab

  /**
   * The desktop footprint: docked against the sidebar's column, full height,
   * with the canvas open to its right. `left` and `width`
   * arrive from App — the same numbers it feeds `setViewInset` — so the box and
   * the camera's idea of the box are one computation. The slide matches the
   * minimap's, for the same reason: the sidebar collapsing moves this pane, and
   * a cut where everything beside it eases reads as a glitch.
   */
  const shell: CSSProperties = narrow
    ? SHEET
    : {
        position: 'absolute',
        // The sidebar's inset as a custom property, with the number App
        // computed as the fallback: the column is docked against the sidebar's
        // right edge, and while that pane's seam is being dragged the property
        // runs ahead of the number, so the column moves with the edge rather
        // than being painted over by it (`SIDEBAR_INSET_VAR`).
        left: `var(${SIDEBAR_INSET_VAR}, ${left}px)`,
        top: 0,
        bottom: 0,
        // The custom property, not the number — see `THREAD_W_VAR`. `width` is
        // still the value of record and is the fallback here, so a first paint
        // before App has written the property is the right size rather than a
        // guess; while a drag is live the property is ahead of it, which is the
        // whole point. And no transition on the width: it follows a pointer, and
        // an eased box would lag the hand that is moving it.
        width: `var(${THREAD_W_VAR}, ${width}px)`,
        zIndex: 'var(--z-float)' as unknown as number,
        pointerEvents: 'auto',
        // One hairline on the field's side: the column is a surface beside the
        // field now, not glass over it (D353).
        borderRight: '1px solid var(--hairline)',
        // …and the slide it rides when the sidebar collapses is taken away for
        // the life of that drag, or the column would ease toward a hand.
        transition: `left var(${SIDEBAR_SLIDE_VAR}, 380ms) cubic-bezier(0.22, 1, 0.36, 1)`,
      }

  return (
    <GlassPane
      ariaLabel="Thread"
      shellStyle={shell}
      radius={narrow ? undefined : 0}
      // Docked, its own ground beside the sidebar's, as the owner's design lays
      // the two columns (D353). The phone's sheet floats over the graph, and
      // keeps the glass.
      solid={narrow ? undefined : 'var(--pane-thread)'}
    >
      {/* The phone sheet's grab bar, on a row of its own above the tabs: the
          platform's own signifier for "this closes", and a plain button, so it
          needs no pointer bookkeeping. At this width there is no ⌘] and no Escape
          key to fall back on. Out of the tab order and the tree: the ✕ at the
          end of the tabs is the same door, and a screen reader met "Close the
          thread" twice in a row when both were in it. A thumb still has the
          whole bar. */}
      {narrow && (
        <button
          aria-hidden
          tabIndex={-1}
          onClick={() => setRail(false)}
          style={{ flex: 'none', height: 18, display: 'grid', placeItems: 'center' }}
        >
          <span
            aria-hidden
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: 'var(--hairline-strong)',
            }}
          />
        </button>
      )}
      <ThreadHead narrow={narrow} surface={surface} />
      {/* Inset by the pane's gutter, as the tabs above it and every eyebrow in
          the report below are: it had none, and sat flush with the glass. */}
      {surface === 'run' && (
        <div className="tierline" style={{ margin: '12px 20px 0' }}>
          <span className="eyebrow">Runs</span>
        </div>
      )}
      {surface === 'node' ? (
        <NodeSurface narrow={narrow} />
      ) : surface === 'run' ? (
        <RunReport />
      ) : (
        <Overview />
      )}
      {/* The footer stays pinned on the desktop. On the phone it scrolls with
          the card instead (see `NodeSurface`): half a screen cannot afford a
          header, a footer AND an ask box standing still around a keyhole.
          Pinned, it takes the pane's gutter: it had none, and its pills and
          its count sat flush with the glass. */}
      {surface === 'node' && !narrow && footShown && <ThreadFoot gutter account={readFirst} />}
      {/* The composer, at the reading's own foot — the point of the re-homing:
          plain ↵ asks about the selection and the answer arrives in the feed
          directly above, streaming. Desktop only: the phone keeps its floating
          strip, which yields to this very sheet (see App) — the feed carries
          the sheet's own ask box instead. Every popover the composer opens
          portals to the body with viewport coordinates (`Floating` in
          Composer.tsx), so none of them lands inside the pane's clip; the tune
          panel floats above the bar inside the pane and caps itself at 62vh,
          which is under the room above the bar at any window height a bar fits
          in. The rows the composer stacks above its bar — the draft's chips,
          the completions, the warnings — are IN THE FLOW (see `.cabove` in
          Composer.tsx): they take their height from the feed rather than
          standing over its last lines and this foot, which is what an absolute
          column did the moment the composer moved in here. The live line needs
          no seat here any more — the feed IS the live line, at transcript
          depth — and during a run the streaming ticker still rides the
          top-left stack, over the field. */}
      {!narrow && (
        <div
          className="tfoot"
          style={{
            // Shrinkable: when a tall draft meets a short window it is the
            // composer's rows that scroll, never the feed's floor or the bar.
            flex: '0 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: 'var(--s3) var(--s4) var(--s4)',
            // The design's footer: one hairline over it (D356) — the foot's own
            // when the card's foot stands there.
            borderTop: footShown ? undefined : '1px solid var(--hairline)',
          }}
        >
          {/* Not while a shared copy is read first — its line stands in the
              field's corner instead (`ui/SharedCopyBanner.tsx`). */}
          {!readFirst && <Composer docked />}
        </div>
      )}
      {/* The seam, last so it draws over the regions it rides. Desktop only: the
          phone's sheet is full width by design and has nothing to resize. */}
      {!narrow && <ThreadSeam width={width} stageW={stageW} left={left} />}
    </GlassPane>
  )
}

/**
 * The seam between the column and the field: the shared separator
 * (`components/PaneSeam.tsx` has the mechanics — the capture, the cancel that
 * is not a commit, the keyboard) with this column's own arithmetic and
 * destination. While a drag is live the width is written straight to the
 * custom property — see `THREAD_W_VAR` for why it may not go through the store
 * — and the store is written once, at the end.
 */
function ThreadSeam({ width, stageW, left }: { width: number; stageW: number; left: number }) {
  const setThreadWidth = useStore((s) => s.setThreadWidth)
  const { min, max } = threadBounds(stageW, left)
  return (
    <PaneSeam
      width={width}
      min={min}
      max={max}
      step={THREAD_STEP}
      label="Resize the thread column"
      paint={(w) => document.documentElement.style.setProperty(THREAD_W_VAR, `${w}px`)}
      commit={setThreadWidth}
      stepBy={(by) => stepThreadWidth(width, by, stageW, left)}
    />
  )
}

/**
 * The Node tab: the anchor's card, then the conversation, in ONE scroll.
 *
 * The card opens on selection — a node selected is a node somebody wants to
 * read — and folds to the one-line strip only when the reader asks it to, for
 * that selection alone: the next selection opens its own card again. With
 * nothing selected the anchor falls back to the seed and the strip is folded
 * by default, because then the chat owns the column (`seedOpenLatch`).
 *
 * THE CARD IS HIDDEN, NEVER UNMOUNTED, and that is load-bearing rather than
 * an optimisation. The E and R keys, the promote presses and the typed
 * `/record` and `/edit` all reach the card by BROADCAST — a counter the card
 * compares against what it saw at mount — so a card mounted BY the very press
 * that poked it records the already-bumped value and swallows the signal
 * (D42's frame, at a new door). A card that is merely invisible was mounted
 * long before the poke and hears it; the watcher below then opens it and
 * brings the form the poke opened onto the screen.
 */
function NodeSurface({ narrow }: { narrow: boolean }) {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const select = useStore((s) => s.select)
  const revealNode = useStore((s) => s.revealNode)
  const selectedId = useStore((s) => s.selectedId)
  const { folded } = useGraphFrame()

  const thread = useMemo(() => threadFor(session, selectedId), [session, rev, selectedId])
  const anchorId = thread.anchorId
  const selected = anchorId !== null && anchorId === selectedId
  /** The selection the reader folded the card for, if they did. Keyed by id,
   *  so a new selection is a card that has not been folded. */
  // One selection starts folded without being asked: a fresh exploration's seed,
  // which the app selects when the question is sent, not the reader. The pane
  // first mounts at that moment, mid-way through the opening pass, and a card
  // seated at the top of the one scroll pushed the pass the newcomer came to
  // watch below the fold. Folded, the conversation follows the pass as it did
  // before the card joined the scroll, and the seed's card is the strip's one
  // press away. Decided when the selection arrives, so it holds as the ring
  // lands rather than springing open with the first child.
  const freshSeed = (id: string | null) =>
    id !== null &&
    id === selectedId &&
    session.order.length === 1 &&
    session.nodes[id]?.parentId === null
  const [hiddenFor, setHiddenFor] = useState<string | null>(() =>
    freshSeed(anchorId) ? anchorId : null,
  )
  const [anchorSeen, setAnchorSeen] = useState(anchorId)
  if (anchorId !== anchorSeen) {
    setAnchorSeen(anchorId)
    if (freshSeed(anchorId)) setHiddenFor(anchorId)
  }
  const [seedOpen, setSeedOpen] = useState(seedOpenLatch)
  // On a desktop the card is the field's until somebody asks for its details
  // (D354, `cardDetails.ts`): the thread opens on the conversation, as the
  // owner's design draws it. The phone's sheet keeps its own fold.
  const details = useDetailsFor()
  const open =
    anchorId !== null &&
    (narrow ? (selected ? hiddenFor !== anchorId : seedOpen) : details === anchorId)
  // Hide and Open card each unmount the button that was pressed — the strip
  // replaces the card's Hide and the card replaces the strip — and focus fell to
  // the page body both ways. It goes to what replaced the press: the strip, or
  // the opened card's title. Only after one of these two presses; a card that
  // opens because a new node was selected leaves focus where the reader has it.
  const seatAfterToggle = useRef<'strip' | 'card' | null>(null)
  const stripRef = useRef<HTMLButtonElement | null>(null)
  const show = () => {
    seatAfterToggle.current = 'card'
    if (!narrow) {
      if (anchorId) openDetails(anchorId)
      return
    }
    if (selected) setHiddenFor(null)
    else {
      seedOpenLatch = true
      setSeedOpen(true)
    }
  }
  const hide = () => {
    seatAfterToggle.current = 'strip'
    if (!narrow) {
      closeDetails()
      return
    }
    if (selected) setHiddenFor(anchorId)
    else {
      seedOpenLatch = false
      setSeedOpen(false)
    }
  }
  useEffect(() => {
    const want = seatAfterToggle.current
    seatAfterToggle.current = null
    // On a desktop the field card is what replaced the card, so its Details
    // takes the focus Hide leaves — the next frame, once it has drawn.
    if (want === 'strip' && !open) {
      if (stripRef.current) stripRef.current.focus()
      else
        requestAnimationFrame(() =>
          document.querySelector<HTMLElement>('.fieldcard [data-details]')?.focus(),
        )
    } else if (want === 'card' && open && anchorId) focusCardTitle(anchorId)
  }, [open, anchorId])

  // A new claim held is a card nobody has asked for yet: the details close, and
  // the field card stands in for the new one. Before the signals below, so a
  // press that selects and opens in one stroke still lands open.
  useEffect(() => {
    if (!narrow && details !== null && details !== anchorId) closeDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorId])

  // The form signals open the card: the poke's whole meaning is "the form is
  // open on the card", and a folded card would honour it invisibly. On the
  // TRANSITION, the `sigRewrite` pattern — reacting to the value would reopen
  // the card on every later render for a poke long spent. Each then brings
  // its form onto the screen (the R and E keys poke without scrolling), and
  // `sigInspect` — the scrubber's card clamps a claim at three lines and this
  // card is where the whole of it lives — asks for the card itself, which the
  // feed seats at the top of the scroll when `inspected` moves its key.
  const sigRecord = useStore((s) => s.sigRecord)
  const sigRewrite = useStore((s) => s.sigRewrite)
  const sigInspect = useStore((s) => s.sigInspect)
  const [inspected, setInspected] = useState(0)
  const seenSignals = useRef({ sigRecord, sigRewrite, sigInspect })
  useEffect(() => {
    const was = seenSignals.current
    seenSignals.current = { sigRecord, sigRewrite, sigInspect }
    const record = sigRecord !== was.sigRecord
    const rewrite = sigRewrite !== was.sigRewrite
    const inspect = sigInspect !== was.sigInspect
    if (!record && !rewrite && !inspect) return
    const id = useStore.getState().selectedId
    if (narrow) {
      setHiddenFor(null)
      seedOpenLatch = true
      setSeedOpen(true)
    } else {
      const target = id ?? anchorId
      if (target) openDetails(target)
    }
    if (record && id) scrollToOutcomeForm(id)
    if (rewrite && id) scrollToRewriteEditor(id)
    if (inspect) setInspected((n) => n + 1)
  }, [sigRecord, sigRewrite, sigInspect])

  // Every press here reveals before it selects — the palette's and the
  // Breakthroughs list's own pattern. The thread deliberately lists a collapsed
  // anchor's children (`core/thread.ts`), so a chip can name a node the canvas
  // is hiding inside a fold; selecting it bare would pivot the reading onto a
  // card the map cannot show, which is the exact disagreement this column
  // promises not to create. `revealNode` unfolds the ancestry and no-ops when
  // nothing above is folded.
  const pivot = (id: string) => {
    revealNode(id)
    select(id)
  }

  // App mounts the pane only with nodes on the canvas, so an empty thread is
  // the render mid-replacement — the next tick unmounts this pane.
  if (anchorId === null || thread.cards.length === 0) return <Feed narrow={narrow} />

  const ancestors = thread.cards.slice(0, -1)
  const anchor = thread.cards[thread.cards.length - 1]!
  const n = anchor.node
  const tint = `var(--sem-${kindChannelOf(n)})`
  // The card's two rules, at one line's budget: the refuted claim this one
  // stands on is named, and a changed record it already names is not said twice.
  const refuted = refutedUnder(session, n.id)
  const evidenceChange = secondLookChange(evidenceStaleOf(session, n.id)?.changes, refuted)
  // The strip's suffix: the kind, then the card's own clauses from the one list
  // the card and the path's cards read (`stateClauses`, D335) — an insight's
  // prior-art word and "not checked yet" included, because folded, the strip is
  // the card's stand-in and those are the facts that prompt the next act.
  const status = [
    kindLabelOf(n),
    ...stateClauses(n, { refuted, evidenceChange }).map((c) => c.word),
  ]
    .filter(Boolean)
    .join(' · ')

  const head = (
    <div
      className="tcardregion"
      data-open={open || undefined}
      // Closed on a desktop the region is no box at all — the card in it stays
      // mounted, hidden, to hear the presses that open it (above).
      style={!narrow && !open ? { display: 'contents' } : undefined}
    >
      {/* Folded, the card is this one line, and the whole line is the press —
          a chevron-sized target for the one control that brings the card back
          would be the pane's most-wanted door behind its smallest handle. It
          says what it does in words, because a header that did not look like a
          control was the first thing the study found. */}
      {!open && narrow && (
        <button
          ref={stripRef}
          className="selstrip"
          aria-expanded={false}
          title={`Open ${n.label} at full depth`}
          onClick={show}
        >
          <i aria-hidden style={{ background: tint }} />
          <b>{n.label}</b>
          <span className="selstrip-status">{status}</span>
          <span className="selstrip-open">Open card</span>
          {/* Closed, so it points the way every closed fold does (D335). */}
          <Chevron open={false} />
        </button>
      )}
      <div className="tanchor" style={{ display: open ? undefined : 'none' }}>
        <NodeCard node={n} onHide={hide} />
        {anchor.forks.length > 0 && (
          <div className="group" style={{ gap: 'var(--s2)' }}>
            <span className="eyebrow">From here</span>
            <ForkRow forks={anchor.forks} folded={folded} onPick={pivot} />
          </div>
        )}
        {/* The path from the seed, folded like the card's own details: it is
            wayfinding, and above the claim it pushed the claim down; each
            ancestor is still one press that re-anchors the reading. */}
        {ancestors.length > 0 && (
          <Fold id="path" label="The path here" meta={`${ancestors.length} above`}>
            {ancestors.map(({ node, forks }) => {
              const standsOn = refutedUnder(session, node.id)
              return (
                <div key={node.id} className="tstop">
                  <AncestorCard
                    node={node}
                    evidenceChange={secondLookChange(
                      evidenceStaleOf(session, node.id)?.changes,
                      standsOn,
                    )}
                    standsOn={standsOn}
                    onOpen={() => pivot(node.id)}
                  />
                  {forks.length > 0 && <ForkRow forks={forks} folded={folded} onPick={pivot} />}
                </div>
              )
            })}
          </Fold>
        )}
      </div>
      {narrow && <ThreadFoot />}
    </div>
  )

  return <Feed narrow={narrow} head={head} headKey={open ? `${anchorId}#${inspected}` : null} />
}

/**
 * One ancestor on the path, compact: the status line, the label, the claim on
 * one clamped line. The whole card is the press — it re-anchors the thread and
 * the selection with it, which is what walking "up" means here. The marks kept
 * are the ones that change how the claim below should be read; everything else
 * waits for the card to be opened.
 *
 * Exported for the same reason as `NodeCard`'s `Statement`: it is pure, and the
 * rule it shares with that component is pinned by rendering both.
 */
export function AncestorCard({
  node,
  evidenceChange,
  standsOn,
  onOpen,
}: {
  node: ReasonNode
  evidenceChange?: { rebasedAt?: number }
  /** The refuted claim it was reasoned from (`refutedUnder`), which the card
   *  names too — a path walked down a refuted branch said "needs a second look"
   *  at every stop below the refutation, and nothing at all under a result. */
  standsOn?: RefutedUnder | null
  onOpen: () => void
}) {
  const tint = `var(--sem-${kindChannelOf(node)})`
  // The card's clauses, from the card's own list (`stateClauses`, D335): an
  // unchecked claim on the path says so, and an insight says whether its
  // prior-art search has read it. Both halves of whose words these are ride
  // just after the ending, as the card's provenance fold says them: the
  // reader's now, first proposed by the model.
  const clauses = stateClauses(node, { refuted: standsOn, evidenceChange }).map((c) => c.word)
  const by = node.editedAt ? rewriteAuthorship(node, false) : null
  if (by) clauses.splice(endingOf(node) ? 1 : 0, 0, by)
  return (
    <button className="tcard" onClick={onOpen} title={node.claim}>
      <span className="insp-status" style={{ color: tint }}>
        {[kindLabelOf(node), ...clauses].join(' · ')}
      </span>
      <b className="tcard-label">{node.label}</b>
      {/* The seed on the path: its claim IS its label, so the clamped line would
          open with the very words the bold line just printed. One rule with the
          anchor's `Statement` — see `claimRestatesLabel` — so the two cards
          cannot disagree about one node. */}
      {!claimRestatesLabel(node) && <span className="tcard-claim">{node.claim}</span>}
    </button>
  )
}

/**
 * The branches the path does not take, as chips in arrival order. A chip is a
 * pivot — the same `select` a canvas tap makes — and a folded branch carries
 * its count, read from the same frame the canvas draws the stub's caption from.
 */
function ForkRow({
  forks,
  folded,
  onPick,
}: {
  forks: ReasonNode[]
  folded: Map<string, number>
  onPick: (id: string) => void
}) {
  return (
    <div className="tforks">
      {forks.map((f) => {
        const fold = f.collapsed ? (folded.get(f.id) ?? 0) : 0
        return (
          <button
            key={f.id}
            className="tfork"
            style={{ '--fork-tint': `var(--sem-${kindChannelOf(f)})` } as CSSProperties}
            onClick={() => onPick(f.id)}
            title={f.claim}
          >
            <i aria-hidden />
            <span>{f.label}</span>
            {fold > 0 && <span className="tfork-fold">+{fold}</span>}
          </button>
        )
      })}
    </div>
  )
}
